import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconChecks,
  IconCircleMinus,
  IconCopy,
  IconEdit,
  IconHome,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";

import {
  Title,
  Text,
  Grid,
  Paper,
  Tooltip,
  Modal,
  ActionIcon,
  Divider,
  useMantineTheme,
  Flex,
  Stack,
  Badge,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useUseCaseBasic, useUseCaseMatchDetails } from "../../../hooks";
import Loading from "../../../ui/Loading";
import DatasetData from "../Data/DatasetData";

function Details({
  details,
}: {
  details: {
    runId: string;
    recon: string;
    reconName: string;
    action: string;
    actionName: string;
    matchDate: string;
    makerId: string;
    makerDate: string;
    checkerId: string;
    checkerDate: any;
    systemStatus: string;
    confirmationStatus: string;
    userStatus: string;
    comment: string;
    details: any;
    id: string;
    name: string;
  };
}) {
  const theme = useMantineTheme();

  return (
    <Grid gutter={"xs"} columns={12}>
      <Grid.Col span={12}>
        <Flex gap="xs" align={"center"}>
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Type
            </Text>
            <Badge
              radius="xs"
              color={"gray"}
              // variant="light"
            >
              Match
            </Badge>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              ID
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.id}
            </Text>
          </Stack>

          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Details
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.action} / {details.actionName}
            </Text>
          </Stack>

          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Match Date
            </Text>
            <Text size="xs" inline c="dimmed">
              {dayjs(details.makerDate).format("YYYY-MM-DD HH:mm:ss")}
            </Text>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              User Status
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.userStatus ? details.userStatus : "__NONE"}
            </Text>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Confirmation Status
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.confirmationStatus
                ? details.confirmationStatus
                : "__NONE"}
            </Text>
          </Stack>
        </Flex>
      </Grid.Col>
    </Grid>
  );
}

export default function Match() {
  // Search params
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId"),
    matchId = searchParams.get("matchId"),
    runId = searchParams.get("runId"),
    runSeq = searchParams.get("runSeq"),
    recon = searchParams.get("recon"),
    access = searchParams.get("access");

  // Theme, query client and navigation
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: useCase } = useUseCaseBasic(entityId, id);

  const { data: matchDetails, error } = useUseCaseMatchDetails(
    entityId,
    id,
    runId,
    runSeq,
    access,
    recon,
    matchId
  );

  const reconDefinition = useCase?.definition.recons.find(
    (r) => r.id === recon
  );

  if (useCase && matchDetails) {
    return (
      <>
        <Stack gap="0">
          <div id="header">
            <Grid
              align="flex-end"
              gutter={"xs"}
              mt="xs"
              mb="xs"
              // mb="xs"
              style={{
                display: "flex",
                // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
              }}
              columns={12}
            >
              <Grid.Col
                span={2}
                mr="xs"
                style={{ borderRight: `2px solid ${theme.colors.dark[3]}` }}
              >
                <Flex align={"end"} gap={theme.spacing.xs}>
                  <Stack gap={0}>
                    <Title order={3} c="dark">
                      {useCase ? useCase.name : "Use Case"}
                    </Title>
                    {useCase && (
                      <Text size="sm" c="dark">
                        {useCase.displayName}
                      </Text>
                    )}
                  </Stack>
                </Flex>
              </Grid.Col>
              <Grid.Col span={9}>
                <Flex align={"flex-end"} gap="xs">
                  {matchDetails && <Details details={matchDetails?.details} />}
                </Flex>
              </Grid.Col>
            </Grid>
          </div>
        </Stack>
        <Divider mt="xs" />

        {reconDefinition && (
          <Stack gap="0" mt="xs" mb="lg">
            {reconDefinition.definition.datasets.map((d) => {
              const initialValues = matchDetails[d.id.toUpperCase()];
              return (
                <DatasetData
                  key={d.id}
                  entityId={entityId}
                  id={id}
                  recon={reconDefinition.id}
                  runId={runId}
                  runSeq={runSeq}
                  access={access}
                  dataset={d}
                  type="MATCH"
                  status="__NONE"
                  queryId={matchDetails.details.id}
                  initialValues={initialValues}
                />
              );
            })}
          </Stack>
        )}
      </>
    );
  } else {
    if (error) {
      return (
        <Grid
          align="flex-start"
          gutter={"xs"}
          m="0"
          mt="lg"
          mb="xs"
          pl="md"
          pr="md"
          style={{
            display: "flex",
            alignItems: "flex-end",
            borderLeft: "5px solid #12B886",
          }}
        >
          <Grid.Col
            span={4}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Paper
              shadow="xs"
              p="xs"
              style={{ width: "100%", textAlign: "center" }}
            >
              <Text c="gray.7" fw="bold">
                No details for match - {matchId}
              </Text>
            </Paper>
          </Grid.Col>
        </Grid>
      );
    } else {
      return (
        <Grid align="center" gutter={"xs"} mb="xs" mt="lg">
          <Grid.Col span={4}>
            <Loading />
          </Grid.Col>
        </Grid>
      );
    }
  }
}
